import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, BookOpen, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/ui/SectionCard";

interface HomeworkAssignment {
  id: number;
  skillArea: string;
  drillName: string;
  description: string;
  durationMinutes: number;
  frequency: string;
  totalDays: number;
  startDate: string;
  endDate: string;
  status: string;
  completionRate: number;
  completionsCount: number;
  videoUrl?: string;
}

interface HomeworkCardProps {
  kidId: string;
}

// Map drill tags to user-friendly labels
const getDrillLabel = (skillArea: string): string => {
  const labelMap: Record<string, string> = {
    hip_shoulder_separation: "Hip–Shoulder Separation",
    front_leg_brace: "Front-Leg Brace",
    batting_balance: "Batting Balance",
    general_foundation: "General Foundation",
  };
  return labelMap[skillArea] || skillArea;
};

export function HomeworkCard({ kidId }: HomeworkCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: homeworkData, isLoading } = useQuery({
    queryKey: [`/api/sessions/parent/kids/${kidId}/homework`],
    queryFn: async () => {
      console.log(`[HOMEWORK CARD] Fetching homework for kidId: ${kidId}`);
      try {
        const response = await api.get(`/api/sessions/parent/kids/${kidId}/homework`);
        console.log(`[HOMEWORK CARD] ✅ Success:`, { 
          success: response.success, 
          homeworkCount: response.data?.homework?.length || 0
        });
        return response;
      } catch (err: any) {
        console.error(`[HOMEWORK CARD] ❌ Error:`, {
          status: err.response?.status,
          message: err.response?.data?.message,
        });
        throw err;
      }
    },
    enabled: !!kidId,
  });

  const homework: HomeworkAssignment[] = homeworkData?.data?.homework || [];

  // Don't render anything if no homework or still loading
  if (isLoading || homework.length === 0) {
    return null;
  }

  // Show up to 3 items in the preview
  const previewItems = homework.slice(0, 3);

  return (
    <div className="space-y-4" data-testid="parent-homework-card">
      {/* Main Homework Card */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-5 w-5 text-orange-600" />
              <h3 className="text-lg font-semibold text-orange-900">Action Required</h3>
            </div>
            <p className="text-orange-700 mb-4">Homework assigned this week</p>
            
            {/* Preview List */}
            <div className="space-y-2 mb-4">
              {previewItems.map((assignment) => (
                <div key={assignment.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-orange-900 text-sm">
                      {getDrillLabel(assignment.skillArea)}
                    </p>
                    <p className="text-orange-700 text-xs line-clamp-2">
                      {assignment.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              variant="outline"
              size="sm"
              className="border-orange-300 text-orange-700 hover:bg-orange-100 hover:border-orange-400"
              data-testid="parent-homework-view-btn"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  View Homework
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded Homework List */}
      {isExpanded && (
        <SectionCard title="Homework Assignments" testId="parent-homework-list">
          <div className="space-y-4">
            {homework.map((assignment) => (
              <div
                key={assignment.id}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-4 hover:bg-slate-100 transition-colors"
                data-testid={`parent-homework-item-${assignment.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-slate-900">
                        {getDrillLabel(assignment.skillArea)}
                      </h4>
                      <span className="px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded-full">
                        {assignment.status}
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm mb-3">
                      {assignment.description}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {assignment.durationMinutes} min {assignment.frequency}
                      </div>
                      <div>
                        Duration: {assignment.totalDays} days
                      </div>
                      <div>
                        Progress: {assignment.completionRate}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}